import { useCallback, useEffect, useRef } from "react";
import { send_request } from "../utils/Request";

const HEARTBEAT_INTERVAL_MS = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ["click", "keydown", "scroll", "touchstart"];

export function useLastActiveHeartbeat(enabled) {
  const lastAttemptedAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const sendHeartbeat = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled || document.visibilityState === "hidden") return;

      const now = Date.now();
      if (!force && now - lastAttemptedAtRef.current < HEARTBEAT_INTERVAL_MS)
        return;
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        await send_request("authz-service/update-last-active", "", {}, "POST");
        lastAttemptedAtRef.current = now;
      } catch (_) {
        // Activity tracking is best-effort and must not interrupt user workflows.
      } finally {
        inFlightRef.current = false;
      }
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    sendHeartbeat({ force: true });

    const handleActivity = () => sendHeartbeat();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        sendHeartbeat();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = window.setInterval(() => {
      sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [enabled, sendHeartbeat]);
}
